// iter148 — Anasayfa CMS + Hero Slider destekli yeniden yazım
// iter154.3 — useSEO ile dinamik meta + ItemList JSON-LD (sitelinks için)
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Link } from "react-router-dom";
import ProductCard from "../../components/public/ProductCard";
import HeroSlider from "../../components/public/HeroSlider";
import RecentlyViewed from "../../components/public/RecentlyViewed";
import AutoCategorySection from "../../components/public/AutoCategorySection";
import { useCompany } from "../../context/CompanyContext";
import useSEO from "../../hooks/useSEO";
import useShopTree from "../../hooks/useShopTree";
import { getTopCategoryIds } from "../../lib/personalization";
import { Button } from "../../components/ui/button";
import {
  ArrowRight, Sparkles, ShieldCheck, Layers3, ThumbsUp,
  BadgePercent, Flame, Package, Tag, Store,
} from "lucide-react";

// Yatay ürün şeridi + Tümünü Gör link'i
function ProductRow({ title, sub, to, icon: Icon, items, iconClass, brandPrimary, dense }) {
  if (!items?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 py-6" data-testid={`row-${(to || "").replace(/[^a-z0-9]+/gi, "-")}`}>
      <div className="flex items-end justify-between mb-4">
        <div>
          {Icon && (
            <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold mb-1 ${iconClass || ""}`}>
              <Icon size={13}/> {sub}
            </div>
          )}
          <h2 className="font-display font-bold text-2xl text-zinc-900">{title}</h2>
        </div>
        {to && (
          <Link to={to} className="text-sm font-medium flex items-center gap-1 hover:opacity-80 shrink-0" style={{ color: brandPrimary }}>
            Tümünü Gör <ArrowRight size={14}/>
          </Link>
        )}
      </div>
      <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-${dense ? 5 : 4} xl:grid-cols-5 gap-3`}>
        {items.slice(0, 10).map((p) => <ProductCard key={p.id} p={p}/>)}
      </div>
    </section>
  );
}

// Büyük banner-tarzı kategori spotlight
function CategorySpotlight({ section, items, brandPrimary }) {
  if (!items?.length && !section.image_url) return null;
  const cat = section.category || {};
  const bg = section.image_url || cat.image;
  const accent = section.accent_color || brandPrimary;
  return (
    <section className="max-w-7xl mx-auto px-4 py-8" data-testid={`spotlight-${section.id}`}>
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to={section.link || `/kategori/${cat.slug || cat.id}`}
          className="relative rounded-2xl overflow-hidden aspect-[4/5] md:aspect-auto md:min-h-[400px] group"
          style={{ background: bg ? `linear-gradient(160deg, rgba(0,0,0,0.15), rgba(0,0,0,0.55)), url(${bg}) center/cover` : `linear-gradient(135deg, ${accent}, #18181b)` }}
        >
          <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
            <div className="text-xs uppercase tracking-widest font-bold mb-2 opacity-90">{section.subtitle || "Öne Çıkan Koleksiyon"}</div>
            <div className="font-display font-bold text-3xl mb-2 leading-tight">{section.title || cat.name}</div>
            <div className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all">
              Keşfet <ArrowRight size={14}/>
            </div>
          </div>
        </Link>
        <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          {items.slice(0, 6).map((p) => <ProductCard key={p.id} p={p}/>)}
        </div>
      </div>
    </section>
  );
}

// Marka spotlight — 2 kolonlu geniş banner
function BrandSpotlight({ section, items, brandPrimary }) {
  if (!items?.length) return null;
  const br = section.brand || {};
  const accent = section.accent_color || brandPrimary;
  return (
    <section className="bg-gradient-to-r from-zinc-50 via-white to-zinc-50 border-y border-zinc-100 py-8 mt-4" data-testid={`brand-spot-${section.id}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            {br.logo && <img src={br.logo} alt={br.name} className="h-14 w-auto object-contain"/>}
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: accent }}>{section.subtitle || "Marka Vitrini"}</div>
              <h2 className="font-display font-bold text-2xl text-zinc-900">{section.title || br.name}</h2>
            </div>
          </div>
          <Link to={section.link || `/urunler?marka=${br.id}`} className="text-sm font-semibold flex items-center gap-1 hover:opacity-80" style={{ color: accent }}>
            {br.name || "Marka"} Ürünleri <ArrowRight size={14}/>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.slice(0, 10).map((p) => <ProductCard key={p.id} p={p}/>)}
        </div>
      </div>
    </section>
  );
}

// Text banner section (görsel + kısa metin + CTA)
function TextBanner({ section, brandPrimary }) {
  const accent = section.accent_color || brandPrimary;
  return (
    <section className="max-w-7xl mx-auto px-4 py-6" data-testid={`banner-${section.id}`}>
      <Link
        to={section.link || "/urunler"}
        className="block relative rounded-2xl overflow-hidden group h-40 md:h-56"
        style={{ background: section.image_url
          ? `linear-gradient(90deg, rgba(0,0,0,0.55), rgba(0,0,0,0.1)), url(${section.image_url}) center/cover`
          : `linear-gradient(135deg, ${accent}, #18181b)` }}
      >
        <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-12 text-white">
          {section.subtitle && <div className="text-xs uppercase tracking-widest font-bold mb-2 opacity-90">{section.subtitle}</div>}
          <div className="font-display font-bold text-2xl md:text-4xl mb-3 max-w-xl">{section.title}</div>
          <div className="inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all">
            <span>Devamını Gör</span> <ArrowRight size={14}/>
          </div>
        </div>
      </Link>
    </section>
  );
}

// Kategori kartları grid (görsel + alt kategoriler önizleme) — iter155: KALDIRILDI (Home'dan çıkarıldı)
// eslint-disable-next-line no-unused-vars
function CategoryGrid_UNUSED({ tree, brandPrimary }) {
  const visible = tree.filter((c) => (c.product_count || 0) > 0 || (c.children || []).length > 0).slice(0, 12);
  if (!visible.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 py-8" data-testid="home-category-grid">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold" style={{ color: brandPrimary }}>
          <Package size={13}/> Kategoriler
        </div>
        <h2 className="font-display font-bold text-2xl md:text-3xl text-zinc-900 mt-1">Ne aramak istersiniz?</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {visible.map((c) => (
          <Link
            key={c.id}
            to={`/kategori/${c.slug || c.id}`}
            data-testid={`home-cat-tile-${c.slug || c.id}`}
            className="group relative border border-zinc-200 rounded-xl overflow-hidden bg-white hover:shadow-lg hover:border-primary/40 transition-all"
          >
            <div className="aspect-square bg-zinc-100 overflow-hidden">
              {c.image
                ? <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                : <div className="w-full h-full flex items-center justify-center"><Package size={36} className="text-zinc-300"/></div>}
            </div>
            <div className="p-2.5">
              <div className="font-semibold text-sm text-zinc-900 truncate group-hover:text-primary">{c.name}</div>
              <div className="text-[11px] text-zinc-500">{c.product_count || 0} ürün</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { company } = useCompany();
  const brandName = company?.company_name || "Bordo Seramik";
  const tagline = company?.tagline || "Yaşama değer katan detaylar";
  const brandPrimary = company?.brand_primary || "#8D1B3D";

  const [showcase, setShowcase] = useState([]);        // CMS sections
  const { tree } = useShopTree();
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [autoSections, setAutoSections] = useState([]); // iter156: kategori bazlı otomatik dolum
  const [sectionItems, setSectionItems] = useState({}); // sectionId → items[]
  const [aboutPage, setAboutPage] = useState(null);

  useEffect(() => {
    // Kritik: ilk paint için sadece hero+showcase+auto. Featured/newest lazy.
    (async () => {
      try {
        const prefer = getTopCategoryIds(6).join(",");
        const [sc, autoR] = await Promise.all([
          api.get("/shop/homepage-showcase"),
          api.get(`/shop/homepage-auto?prefer=${encodeURIComponent(prefer)}&limit_sections=6&per_section=8&min_products=3`),
        ]);
        setShowcase(sc.data || []);
        setAutoSections(autoR.data?.sections || []);
      } catch { /* ignore */ }
      // Lazy: yeni gelenler + öne çıkanlar
      Promise.all([
        api.get("/shop/products?featured=true&limit=10").catch(() => ({ data: { items: [] } })),
        api.get("/shop/products?limit=10").catch(() => ({ data: { items: [] } })),
      ]).then(([f, ne]) => {
        setFeatured(f.data.items || []);
        setNewest(ne.data.items || []);
      });
      // Hakkımızda (arka plan)
      api.get("/seo/pages/hakkimizda").then(r => setAboutPage(r.data)).catch(() => {});
    })();
  }, []);

  // Section ürünlerini yükle (parallel)
  useEffect(() => {
    if (!showcase.length) return;
    (async () => {
      const results = await Promise.all(showcase.map(async (s) => {
        try {
          const params = new URLSearchParams();
          const lim = s.limit || 10;
          params.set("limit", String(lim));
          if (s.category_id) params.set("category_id", s.category_id);
          if (s.brand_id) params.set("brand_id", s.brand_id);
          if ((s.product_ids || []).length === 0) {
            const { data } = await api.get(`/shop/products?${params.toString()}`);
            return [s.id, data.items || []];
          } else {
            // Sabit ürün listesi
            const items = [];
            for (const pid of s.product_ids.slice(0, lim)) {
              try {
                const { data } = await api.get(`/shop/products/${pid}`);
                if (data) items.push(data);
              } catch { /* ignore */ }
            }
            return [s.id, items];
          }
        } catch { return [s.id, []]; }
      }));
      const map = {};
      results.forEach(([sid, items]) => { map[sid] = items; });
      setSectionItems(map);
    })();
  }, [showcase]);

  const renderSection = (s) => {
    const items = sectionItems[s.id] || [];
    switch (s.type) {
      case "category_spotlight":
      case "spotlight":
        return <CategorySpotlight key={s.id} section={s} items={items} brandPrimary={brandPrimary}/>;
      case "brand_carousel":
      case "brand_grid":
      case "brand_spotlight":
        return <BrandSpotlight key={s.id} section={s} items={items} brandPrimary={brandPrimary}/>;
      case "text_banner":
        return <TextBanner key={s.id} section={s} brandPrimary={brandPrimary}/>;
      case "category_grid":
      case "category_carousel":
      case "featured_products":
      default:
        return (
          <ProductRow
            key={s.id}
            title={s.title || (s.category?.name || s.brand?.name || "Öne Çıkanlar")}
            sub={s.subtitle || "Kategori"}
            icon={s.type === "featured_products" ? Sparkles : Tag}
            iconClass={s.type === "featured_products" ? "text-primary" : "text-zinc-500"}
            to={s.link || (s.category?.slug ? `/kategori/${s.category.slug}` : (s.brand?.id ? `/urunler?marka=${s.brand.id}` : "/urunler"))}
            items={items}
            brandPrimary={brandPrimary}
          />
        );
    }
  };

  const activeSections = showcase.filter((s) => s.active !== false);

  // iter154.3 — SEO: dinamik ItemList (top kategoriler + kampanyalar) sitelinks için
  const homeJsonLd = useMemo(() => {
    const base = (typeof window !== "undefined" ? window.location.origin : "https://bordoseramik.com");
    const items = [];
    const topCats = (tree.categories || []).filter(c => (c.product_count || 0) > 0).slice(0, 8);
    topCats.forEach((c, i) => items.push({
      "@type": "SiteNavigationElement",
      "position": i + 1,
      "name": c.name,
      "url": `${base}/kategori/${c.slug || c.id}`,
      "description": `${c.product_count || 0} ürün · Adana teslimat`,
    }));
    const nav = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Bordo Seramik — Kategoriler",
      "numberOfItems": items.length,
      "itemListElement": items,
    };
    // Ana sayfa özel: CollectionPage — Google için sitelinks güçlü sinyal
    const collection = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `${brandName} — Ana Sayfa`,
      "description": "Adana Çukurova, Sarıçam ve Seyhan mağazalarında 15.000+ seramik, granit, banyo ve mutfak malzemesi. Ücretsiz keşif, uygun fiyat, 24 saat teslimat.",
      "url": base,
      "mainEntity": nav,
      "isPartOf": {"@id": `${base}/#website`},
    };
    return [nav, collection];
  }, [tree.categories, brandName]);

  useSEO({
    // title'ı undefined bırakırsak useSEO seo_settings.site_title'ı kullanır
    description: `Adana Çukurova, Sarıçam ve Seyhan'da 3 mağazasıyla 15.000+ seramik, granit, banyo ve mutfak malzemesi. TSE/CE sertifikalı, ücretsiz keşif, uygun fiyat, 24 saat teslimat. 14+ yıllık güvenilir tedarikçi.`,
    canonical: "/",
    jsonld: homeJsonLd,
  });

  return (
    <div>
      {/* HERO SLIDER (CMS-yönetilebilir karşılama) */}
      <HeroSlider brandName={brandName} tagline={tagline} brandPrimary={brandPrimary}/>

      {/* iter148.1 — Son Baktıklarınız (varsa üstte gözüksün, dönüşü artır) */}
      <RecentlyViewed brandPrimary={brandPrimary}/>

      {/* iter156 — Otomatik Kategori Vitrinleri (personalize) */}
      {autoSections.map((s, i) => (
        <AutoCategorySection key={s.id} section={s} brandPrimary={brandPrimary} isFirst={i === 0}/>
      ))}

      {/* CMS Vitrin Bölümleri (admin sıralar) */}
      {activeSections.length > 0 ? (
        activeSections.map(renderSection)
      ) : (
        <>
          {/* Fallback: Featured + Yeni Gelenler */}
          <ProductRow
            title="Öne Çıkan Ürünler" sub="Editörün Seçimi" icon={Sparkles} iconClass="text-primary"
            to="/urunler?featured=1" items={featured} brandPrimary={brandPrimary}/>
          <ProductRow
            title="Yeni Gelenler" sub="Son Eklenenler" icon={BadgePercent} iconClass="text-zinc-500"
            to="/urunler" items={newest} brandPrimary={brandPrimary}/>
        </>
      )}

      {/* Kurumsal değerler — 4 kart, sade & şık tek renk tonu */}
      <section className="bg-white py-20 mt-6 border-t border-b border-zinc-100">
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: brandPrimary }}>Neden {brandName}</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-zinc-900 mt-3 tracking-tight">Alışverişinizde farkı ilk andan hissedeceksiniz</h2>
            <p className="text-sm text-zinc-500 mt-4 leading-relaxed">2014'ten bu yana; ödeme kolaylığı, hızlı teslim, kalite güvencesi ve müşteri odaklı hizmet anlayışıyla yanınızdayız.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-100 border border-zinc-100 rounded-2xl overflow-hidden">
            {[
              { icon: BadgePercent, t: "Ödeme Kolaylığı", d: "Nakit, kredi kartı, taksit, havale ve çek — size en uygun ödeme yöntemini birlikte belirleyelim." },
              { icon: Store, t: "Mağazadan veya Depodan Teslim", d: "Ürünü fiziksel mağazamızdan görüp alın ya da depomuzdan aynı gün teslim alma seçeneğinden faydalanın." },
              { icon: ShieldCheck, t: "Kalite Güvencesi", d: "Kalite kontrolünden geçmemiş ürün rafımıza girmez. TSE/CE sertifikalı, yıllar sonra bile memnun edecek malzemeler." },
              { icon: ThumbsUp, t: "Müşteri Memnuniyeti Odaklı", d: "Satış öncesi keşiften satış sonrası desteğe kadar — kararınızda yalnız kalmayın, uzman ekibimiz her adımda yanınızda." },
            ].map((v, i) => {
              const I = v.icon;
              return (
                <div key={i} className="group relative bg-white p-8 hover:bg-zinc-50/50 transition-colors duration-300" data-testid={`value-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-zinc-50 border border-zinc-100 group-hover:border-primary/20 group-hover:bg-primary/5 transition-all">
                    <I size={22} strokeWidth={1.75} className="text-zinc-700 group-hover:text-primary transition-colors"/>
                  </div>
                  <div className="font-display font-semibold text-[17px] text-zinc-900 mb-3 tracking-tight leading-snug">{v.t}</div>
                  <p className="text-[13px] text-zinc-500 leading-relaxed">{v.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marka şeridi — logo + isim */}
      {tree.brands?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-10">
          <div className="text-center mb-6">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Güvenilir Markalarımız</div>
            <h3 className="font-display font-semibold text-xl text-zinc-900 mt-1">Kalite garantili marka portföyü</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {tree.brands.slice(0, 18).map((b) => (
              <Link key={b.id} to={`/urunler?marka=${b.id}`}
                    className="group flex items-center gap-2 bg-white border border-zinc-100 hover:border-primary/40 hover:shadow-md hover:shadow-zinc-900/5 rounded-xl p-2.5 transition-all"
                    data-testid={`brand-strip-${b.id}`} title={b.name}>
                <div className="w-11 h-11 rounded-lg bg-zinc-50 flex items-center justify-center shrink-0 overflow-hidden">
                  {b.logo
                    ? <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain opacity-90 group-hover:opacity-100 transition"/>
                    : <span className="text-xs font-bold text-zinc-500 group-hover:text-primary">{(b.name || "?")[0]}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-zinc-900 truncate group-hover:text-primary">{b.name}</div>
                  {b.product_count > 0 && <div className="text-[9px] uppercase tracking-widest text-zinc-400">{b.product_count} ürün</div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer stats — iter155: hakkimizda sayfasından çekilir; yoksa hiç gösterme */}
      {aboutPage?.sections?.some(s => s.kind === "stats" || s.kind === "rich_text") && (
        <section className="bg-zinc-950 text-white py-14" data-testid="home-about-stats">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="text-center mb-8">
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: brandPrimary }}>Hakkımızda</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl mt-1">{aboutPage.hero_title || "Bordo Seramik'i Tanıyın"}</h2>
              {aboutPage.hero_subtitle && <p className="text-sm text-white/60 mt-2 max-w-2xl mx-auto">{aboutPage.hero_subtitle}</p>}
            </div>
            {(() => {
              const statsSec = aboutPage.sections.find(s => s.kind === "stats");
              if (statsSec?.items?.length) {
                return (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {statsSec.items.slice(0, 4).map((it, i) => (
                      <div key={i}>
                        <div className="text-4xl md:text-5xl font-display font-bold tracking-tight" style={{ color: brandPrimary }}>{it.value || it.n || "—"}</div>
                        <div className="text-[11px] uppercase tracking-widest mt-2 text-white/60">{it.label || it.l || ""}</div>
                      </div>
                    ))}
                  </div>
                );
              }
              // Fallback: rich_text bölümlerinden ilk 2 tanesini kısa özet olarak göster
              const richs = aboutPage.sections.filter(s => s.kind === "rich_text").slice(0, 2);
              return (
                <div className="grid md:grid-cols-2 gap-6 text-sm text-white/80 leading-relaxed max-w-4xl mx-auto">
                  {richs.map((s, i) => (
                    <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                      {s.heading && <div className="font-display font-semibold text-lg mb-2 text-white">{s.heading}</div>}
                      <p className="line-clamp-6">{s.body}</p>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="mt-8 text-center">
              <Link to="/hakkimizda" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest text-white/90 border border-white/20 hover:bg-white/10 transition-all">
                Devamını Oku →
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
